import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Heart } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';

/**
 * Berapa detik sebelum pengguna diantar sendiri ke dashboard.
 *
 * Tiga puluh detik, bukan lima. Orang yang baru saja membayar biasanya masih
 * membaca, memotret layar, atau mencatat nomor pesanannya. Mengantarnya pergi
 * setelah lima detik memutus itu di tengah jalan.
 */
const HITUNG_MUNDUR = 30;

/**
 * Halaman yang dituju setelah pembayaran selesai di sisi Duitku.
 *
 * Ini BUKAN bukti pembayaran, dan sengaja tidak berpura-pura begitu. Yang
 * mengaktifkan paket adalah callback dari penyedia ke server, bukan
 * pengalihan peramban ini. Pengguna bisa saja sampai di sini setelah
 * membatalkan, atau bahkan membuka alamatnya langsung.
 *
 * Karena itu kalimatnya berterima kasih atas dukungannya, bukan menyatakan
 * "pembayaran Anda berhasil". Keadaan sebenarnya dibaca ulang dari server
 * saat pengguna tiba di dashboard.
 */
export const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status } = useAuthStore();
  const [sisa, setSisa] = useState(HITUNG_MUNDUR);

  const sudahMasuk = status === 'authenticated';
  const tujuan = sudahMasuk ? '/dashboard' : '/masuk';

  // Kuota dan tagihan dibaca ulang begitu halaman ini terbuka, supaya
  // dashboard yang dituju sudah menampilkan keadaan terbaru, bukan angka
  // yang tersimpan sebelum pembayaran.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['usage'] });
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['addon-active'] });
  }, [queryClient]);

  useEffect(() => {
    if (sisa <= 0) {
      navigate(tujuan, { replace: true });
      return undefined;
    }
    const id = setTimeout(() => setSisa((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [sisa, navigate, tujuan]);

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--surface-canvas)] px-4">
      <div className="w-full max-w-lg text-center">
        <Link to="/" className="mb-8 inline-flex">
          <Logo className="h-8 w-auto" />
        </Link>

        <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-8 shadow-sm">
          <div className="mx-auto grid size-16 place-items-center rounded-full bg-success-50 dark:bg-success-500/10">
            <CheckCircle2 className="size-8 text-success-600" aria-hidden="true" />
          </div>

          <h1 className="mt-5 text-2xl font-bold text-[var(--text-primary)]">Terima kasih</h1>

          <p className="mt-3 leading-relaxed text-[var(--text-secondary)]">
            Dukungan Anda yang membuat Crawel bisa terus tumbuh. Tiap langganan langsung menjadi
            server yang lebih kuat, fitur yang lebih matang, dan waktu untuk memperbaiki hal-hal
            kecil yang bikin repot.
          </p>

          <p className="mt-4 text-sm leading-relaxed text-[var(--text-muted)]">
            Kuota Anda bertambah otomatis begitu pembayaran diterima, biasanya dalam hitungan
            detik. Kalau belum berubah saat Anda sampai di dashboard, muat ulang sebentar lagi,
            atau buka Paket &amp; Tagihan untuk melihat statusnya.
          </p>

          <div className="mt-7 flex flex-col items-center gap-3">
            <Button
              icon={ArrowRight}
              onClick={() => navigate(tujuan, { replace: true })}
              className="w-full sm:w-auto"
            >
              {sudahMasuk ? 'Ke Dashboard sekarang' : 'Masuk ke akun'}
            </Button>
            <p className="text-xs text-[var(--text-muted)]" role="status" aria-live="polite">
              Anda akan diantar otomatis dalam {sisa} detik
            </p>
          </div>
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-sm text-[var(--text-muted)]">
          <Heart className="size-4 text-danger-500" aria-hidden="true" />
          Dibuat di Indonesia
        </p>
      </div>
    </div>
  );
};
